'use client';

import { useEffect, useState } from 'react';

/**
 * 将入参规范为非负整数秒；非法值返回 0
 * @param initialSeconds 初始秒数
 * @returns 规范化后的秒数
 */
function normalizeSeconds(initialSeconds: number): number {
    const n = Number(initialSeconds);
    if (!Number.isFinite(n) || n < 0) {
        return 0;
    }
    return Math.floor(n);
}

/**
 * 倒计时 hook：从初始秒数每秒减 1，到 0 后保持不变。
 * `initialSeconds` 变化时重置倒计时并重建定时器。
 *
 * 前后台漂移（暂未实现，仅保留方案）：
 * - 当前实现是相对秒数累减，后台时 `setInterval` 会被节流/暂停，回前台后显示会偏慢。
 * - 正式活动应优先用绝对截止时间校对，伪代码：
 *   ```
 *   // deadlineRef = expireAt ?? Date.now() + initialSeconds * 1000
 *   // const sync = () => setRemaining(max(0, ceil((deadline - Date.now()) / 1000)))
 *   // setInterval(sync, 1000)
 *   // document.addEventListener('visibilitychange', () => {
 *   //   if (document.visibilityState === 'visible') sync()
 *   // })
 *   ```
 * - 与 ActivityStore `expireAt`、挂件 §6.8「禁止累加、按绝对时间重算」对齐后再落地。
 *
 * @param initialSeconds 初始秒数（大于等于 0）；变化时重置
 * @returns 当前剩余秒数
 */
export function useCountdown(initialSeconds: number) {
    // 当前剩余秒数
    const [remaining, setRemaining] = useState(() => normalizeSeconds(initialSeconds));

    useEffect(() => {
        const next = normalizeSeconds(initialSeconds);
        setRemaining(next);

        // 定时器 id；仅 next > 0 时创建，cleanup 统一清理
        let id: ReturnType<typeof setInterval> | undefined;
        if (next > 0) {
            id = setInterval(() => {
                setRemaining(s => {
                    if (s <= 1) {
                        return 0;
                    }
                    return s - 1;
                });
            }, 1000);
        }

        return () => {
            if (id != null) {
                clearInterval(id);
            }
        };
    }, [initialSeconds]);

    return remaining;
}

/** 倒计时拆分为 天 / 时 / 分 / 秒 */
export interface ICountdownParts {
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
}

const SECONDS_PER_DAY = 86400;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

/** 格式化为两位数字符串，非数字时返回 "00" */
export function pad2(n: number): string {
    const num = Number(n);
    if (!Number.isFinite(num) || num < 0) {
        return '00';
    }
    return String(Math.floor(num)).padStart(2, '0');
}

/**
 * 将总秒数拆分为 天、时、分、秒
 */
export function parseCountdown(seconds: number): ICountdownParts {
    const n = Number(seconds);
    const total = Math.max(0, Math.floor(Number.isFinite(n) ? n : 0));
    const d = Math.floor(total / SECONDS_PER_DAY);
    const h = Math.floor((total % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
    const m = Math.floor((total % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
    const s = total % SECONDS_PER_MINUTE;
    return { days: pad2(d), hours: pad2(h), minutes: pad2(m), seconds: pad2(s) };
}
